import { ReactNode } from "react";
import styled from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { ErrorMessage, useFieldId } from "@pages/AdminDashboard/ui/FieldFrame";
import { TextField } from "@pages/AdminDashboard/ui/TextField";

const [, , spaceMd, spaceLg] = adminTokens.spacing;
// The design's Created from / to fields are 130px wide; a native date field
// needs a little more to show its date and its picker.
const dateFieldWidth = 150;

export interface DateRangeValue {
  from: string;
  to: string;
}

// A year may have more than four digits, so each year is padded to six before the
// dates are compared as text.
const sortableDate = (date: string): string =>
  date.replace(/^(\d+)-/, (_match, year: string) => `${year.padStart(6, "0")}-`);

// A range is invalid only when both dates are set and the to date is before the
// from date (RANGE-R2, R3). A screen disables the Button that sends the search
// while this is true (RANGE-R6).
export const isDateRangeInvalid = (from: string, to: string): boolean =>
  from !== "" && to !== "" && sortableDate(to) < sortableDate(from);

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spaceMd}px;
`;

// On a phone the two dates share a row equally and the end-of-row control takes
// the next row across the full width (PHONE-R34).
const Fields = styled.div<{ $isPhone: boolean }>`
  display: ${({ $isPhone }) => ($isPhone ? "grid" : "flex")};
  grid-template-columns: ${({ $isPhone }) => ($isPhone ? "minmax(0, 1fr) minmax(0, 1fr)" : "none")};
  gap: ${spaceLg}px;
  align-items: flex-end;
`;

const EndOfRow = styled.div`
  grid-column-start: 1;
  grid-column-end: -1;
  display: flex;

  & > * {
    flex: 1;
  }
`;

interface DateRangeProps {
  fromLabel: string;
  toLabel: string;
  from: string;
  to: string;
  onChange: (range: DateRangeValue) => void;
  disabled?: boolean;
  // The message shown when the range is invalid; it names both labels by default.
  invalidMessage?: string;
  // A control for the end of the row, level with the fields (RANGE-R9), such as the
  // Button that sends the search.
  children?: ReactNode;
}

// A from date and a to date side by side, with one message below them when the
// to date is before the from date (RANGE-R1…R8).
export const DateRange = ({
  fromLabel,
  toLabel,
  from,
  to,
  onChange,
  disabled = false,
  invalidMessage = `“${fromLabel}” must be on or before “${toLabel}”.`,
  children,
}: DateRangeProps) => {
  const isPhone = useIsPhoneLayout();
  const messageId = `${useFieldId()}-range-error`;
  const isInvalid = isDateRangeInvalid(from, to);
  const invalidProps = isInvalid
    ? { "aria-invalid": true as const, "aria-describedby": messageId }
    : {};

  return (
    <Stack>
      <Fields $isPhone={isPhone}>
        <TextField
          label={fromLabel}
          type="date"
          width={dateFieldWidth}
          value={from}
          disabled={disabled}
          onChange={(event) => onChange({ from: event.target.value, to })}
          {...invalidProps}
        />
        <TextField
          label={toLabel}
          type="date"
          width={dateFieldWidth}
          value={to}
          disabled={disabled}
          onChange={(event) => onChange({ from, to: event.target.value })}
          {...invalidProps}
        />
        {isPhone && children ? <EndOfRow>{children}</EndOfRow> : children}
      </Fields>
      {isInvalid && (
        <ErrorMessage id={messageId} role="alert">
          {invalidMessage}
        </ErrorMessage>
      )}
    </Stack>
  );
};
